import { TRPCClientError } from "@trpc/client";
import { toast } from "sonner";
import { clearAuthData } from "./auth";
import { debounce } from "./debounce";


export function handleServerError(error: unknown) {
  // Handle 401 Unauthorized - redirect to login
  if (isUnauthorizedError(error)) {
    const { pathname } = window.location;
    if (!pathname.startsWith('/login')) {
      console.log('Redirecting to login page in web environment');
      toast.error('Your session has expired. Please login again.');
      clearAuthData();
      debounce(() => {
        window.location.href = `/login?redirect_uri=${encodeURIComponent(window.location.href)}`;
      }, 1000);
    }
    return;
  }

  // Handle 403 Forbidden - don't show toast, let the component handle it
  if (isForbiddenError(error)) {
    // 403 errors are permission-based, not auth failures
    // The component should display an appropriate "Access Denied" UI
    return;
  }

  let errMsg = 'Something went wrong!'
  let statusCode: number | undefined = undefined;

  // Check for the special redirect error case
  if (
    error instanceof TRPCClientError &&
    error.cause &&
    typeof error.cause === 'object' &&
    'isRedirect' in error.cause &&
    error.cause.isRedirect === true
  ) {
    console.log('Redirect error detected, not showing error toast');
    return; // Don't show error toast for redirect errors
  }

  // Extract status code if available
  if (
    error &&
    typeof error === 'object' &&
    'status' in error &&
    typeof error.status === 'number'
  ) {
    statusCode = error.status;

    if (statusCode === 204) {
      errMsg = 'Content not found.'
    }
  }

  if (error instanceof TRPCClientError) {
    errMsg = error.message

    // Check if the error contains a status code in data.httpStatus
    if (error.data && typeof error.data === 'object' && 'httpStatus' in error.data) {
      statusCode = Number(error.data.httpStatus);
    }
  }

  // Only show error toast for non-auth errors
  toast.error(errMsg);
}


// Function to safely extract HTTP status code from TRPC errors
// Using unknown instead of any for better type safety
function getTRPCErrorHttpStatus(error: unknown): number | undefined {
  if (!error || typeof error !== 'object') return undefined;

  // Type guard
  const errorObj = error as Record<string, unknown>;

  // Check for shape property
  if ('shape' in errorObj && errorObj.shape) {
    const shape = errorObj.shape as Record<string, unknown>;

    // Check for data.httpStatus in shape
    if (shape.data && typeof shape.data === 'object') {
      const data = shape.data as Record<string, unknown>;
      if ('httpStatus' in data && typeof data.httpStatus === 'number') {
        return data.httpStatus;
      }
    }
  }

  return undefined;
}

export function isUnauthorizedError(error: unknown) {
  const statusCode = getTRPCErrorHttpStatus(error);
  return statusCode === 401;
}

export function isForbiddenError(error: unknown) {
  const statusCode = getTRPCErrorHttpStatus(error);
  return statusCode === 403;
}

/**
 * Check if error is an auth-related error (401 or 403)
 * These errors should not be retried
 */
export function isAuthError(error: unknown) {
  const statusCode = getTRPCErrorHttpStatus(error);
  return statusCode === 401 || statusCode === 403;
}

